import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ACTIONS, EVENTS, STATUS } from 'react-joyride'
import { useIntl } from 'react-intl'
import { message } from 'antd'
import { useDispatch, useSelector } from 'react-redux'

import {
  isFinanceOnboardingSelector,
  loadedInitSelector,
  mobileViewSelector,
  optionsSelector,
  tutorialSelector,
} from 'app-redux/selectors'
import { HelpDropdown, ModalPlayer, ProductTour } from 'components/UI'
import Player from 'components/UI/ModalPlayer/Player'
import { getTutorial } from 'utils/apis'
import { editOption } from 'app-redux/options/actions'
import { compareWithJSON } from 'utils/helper'
import tours from './tours'
import tourDropdownGuard, { installTourDropdownGuard } from './tourDropdownGuard'

const Module = ({
  name,
  joyrideCallback,
  joyrideRef,
  hideOnEmpty,
  onPreOpenTourHandler,
  ...props
}) => {
  const dispatch = useDispatch()
  const { formatMessage, messages } = useIntl()
  const tutorial = useSelector((state) => tutorialSelector(state, name))
  const isMobileView = useSelector(mobileViewSelector)
  const loadedInit = useSelector(loadedInitSelector)
  const options = useSelector(optionsSelector)
  // Selama onboarding pertama kali masih aktif (belum profit_loss_viewed) tur per halaman
  // tidak boleh jalan supaya tidak bertabrakan dengan spotlight onboarding.
  const isFinanceOnboarding = useSelector(isFinanceOnboardingSelector)

  const [visiblePlayer, setVisiblePlayer] = useState(false)
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [steps, setSteps] = useState([])

  const tour = useMemo(() => tours[name], [name])

  useEffect(() => {
    if (loadedInit && !isFinanceOnboarding && tutorial?.name && options[`tutorial_${name}`]) {
      openTourHandler()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedInit])

  // Pasang sekali (saat mount, sebelum dropdown pernah dibuka) supaya guard jalan duluan
  // dari listener outside-click antd.
  useEffect(() => {
    installTourDropdownGuard()
  }, [])

  // Aktifkan guard saat step menandai dropdown harus tetap terbuka (mis. step item recurring).
  useEffect(() => {
    const step = steps[stepIndex]
    tourDropdownGuard.active = Boolean(run && step?.keepDropdownOpen)
    return () => {
      tourDropdownGuard.active = false
    }
  }, [run, stepIndex, steps])

  // Step dengan `advanceOnClick`: lanjut otomatis saat user mengklik target-nya.
  // Dipakai mis. tombol "Lainnya" yang harus dibuka dulu agar item di dropdown bisa
  // jadi target step berikutnya. Pakai index absolut (stepIndex + 1) supaya idempotent
  // walau react-joyride ikut mem-fire STEP_AFTER.
  useEffect(() => {
    if (!run) {
      return undefined
    }
    const step = steps[stepIndex]
    if (!step || !step.advanceOnClick) {
      return undefined
    }
    const el = document.querySelector(step.target)
    if (!el) {
      return undefined
    }
    // Kalau target ada di dalam item dropdown antd, pasang listener di item-nya supaya
    // klik di area mana pun pada item (bukan cuma teks) tetap memicu advance.
    const clickTarget = el.closest('.ant-dropdown-menu-item') || el
    const handleAdvance = () => {
      // beri jeda agar dropdown sempat ter-render sebelum pindah step
      setTimeout(() => {
        if (stepIndex + 1 >= steps.length) {
          // step terakhir: tutup tur (samakan dengan perilaku finish)
          setRun(false)
          if (options[`tutorial_${name}`]) {
            dispatch(editOption({ [`tutorial_${name}`]: 0 }))
          }
        } else {
          setStepIndex(stepIndex + 1)
        }
      }, 300)
    }
    clickTarget.addEventListener('click', handleAdvance, { once: true })
    return () => {
      clickTarget.removeEventListener('click', handleAdvance)
    }
  }, [run, stepIndex, steps, dispatch, options, name])

  const joyrideCallbackHandler = useCallback(
    (data) => {
      const { action, status, index, type } = data
      if (action === ACTIONS.CLOSE || [STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        setRun(false)
        if (options[`tutorial_${name}`]) {
          dispatch(editOption({ [`tutorial_${name}`]: 0 }))
        }
      } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
        const stepI = index + (action === ACTIONS.PREV ? -1 : 1)
        setStepIndex(stepI)
      }
      if (typeof joyrideCallback === 'function') {
        joyrideCallback(data, { stepIndex, setStepIndex, run, setRun })
      }
    },
    [dispatch, joyrideCallback, name, options, run, stepIndex],
  )

  const getData = useCallback(async () => {
    try {
      // Tur yang sudah dipindah ke file lokal (utils FE) tidak perlu hit backend
      if (tour) {
        setSteps(formatSteps(tour))
        setStepIndex(0)
        setTimeout(() => {
          setRun(true)
        }, 100)
        return
      }

      const response = await getTutorial(name)
      if (response.data.success) {
        setSteps(formatSteps(response.data.data))
        setStepIndex(0)
        setTimeout(() => {
          setRun(true)
        }, 100)
      }
    } catch (error) {
      message.error((error && error.message) || formatMessage({ id: 'button.error_data_' }))
    } finally {
      message.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTutorial, name, tour])

  const formatSteps = useCallback(
    (data) => {
      return data.map((row) => {
        const newRow = { ...row }

        if (!newRow.placement) {
          delete newRow.placement
        }

        // Resolusi teks konten:
        // - contentId → ambil dari i18n via messages (raw, tanpa parsing ICU agar HTML aman)
        // - content string → data backend apa adanya
        const rawContent = row.contentId ? messages[row.contentId] : row.content

        if (row.videoId) {
          // Step bervideo butuh tooltip lebih lebar; lebar video mengikuti lebar tooltip
          // (video-responsive: width 100% + aspect-ratio), default 380px bikin video kekecilan.
          if (!newRow.width) {
            newRow.width = 480
          }
          newRow.content = (
            <>
              <div dangerouslySetInnerHTML={{ __html: rawContent }} />
              <Player
                containerClassName="video-responsive"
                className="youtube-player mt-4"
                videoId={row.videoId}
                options={{
                  playerVars: {
                    autoplay: 0,
                  },
                }}
              />
            </>
          )
        } else {
          newRow.content = <div dangerouslySetInnerHTML={{ __html: rawContent }} />
        }
        return newRow
      })
    },
    [messages],
  )

  const showPlayerHandler = () => setVisiblePlayer(true)
  const hidePlayerHandler = () => setVisiblePlayer(false)
  const openArticle = () => window.open(tutorial?.article, '_blank')
  const openTourHandler = useCallback(async () => {
    message.loading(formatMessage({ id: 'loading_tour' }), 0)
    // Untuk keperluan jika ada yang perlu di load. Contohnya di gudang
    if (typeof onPreOpenTourHandler === 'function') {
      await onPreOpenTourHandler()
    }

    if (steps.length === 0) {
      getData()
    } else {
      setStepIndex(0)
      setRun(true)
      message.destroy()
    }
  }, [formatMessage, onPreOpenTourHandler, steps, getData])

  if (!loadedInit) {
    return null
  }

  if (hideOnEmpty && Object.keys(tutorial).length === 0) {
    return null
  }

  return (
    <>
      <HelpDropdown
        onClickTour={(tutorial?.name || tour) && !isFinanceOnboarding ? openTourHandler : undefined}
        onClickVideo={tutorial?.video_id ? showPlayerHandler : undefined}
        onClickArticle={tutorial?.article ? openArticle : undefined}
        isMobileView={isMobileView}
        {...props}
      />
      {visiblePlayer && (
        <ModalPlayer
          title={formatMessage({ id: 'tutorial.video_tutorial' })}
          visible={visiblePlayer}
          onCancel={hidePlayerHandler}
          videoId={tutorial?.video_id}
        />
      )}

      <ProductTour
        ref={joyrideRef}
        callback={joyrideCallbackHandler}
        run={run}
        steps={steps}
        spotlightPadding={8}
        stepIndex={stepIndex}
      />
    </>
  )
}

export default memo(Module, compareWithJSON)
